import {INTEGRATION_OPTIONS} from 'constants/integration-options';
import {useCallback, useMemo, useState} from 'react';
import {Integration, IntegrationType} from 'types/integration';
import IntegrationCard from '../IntegrationCard';
import styles from './IntegrationList.module.scss';
import api from 'api';
import {useMutation} from 'react-query';
import toast from 'react-hot-toast';
import {get} from 'lodash';
import GoogleDriveCard from '../GoogleDriveCard';
import useAuth from 'hooks/useAuth';
import FeatureGuard from 'components/FeatureGuard';

type Props = {
  integrations?: Integration[];
  onConnect: (integrationType: IntegrationType) => void;
  onDisconnect?: (integraion?: Integration) => void;
};

const manualSync = async (integrationId: string) => {
  return await api.post(
    `/integrations/integration-configs/${integrationId}/sync/`
  );
};

function IntegrationList({integrations, onConnect, onDisconnect}: Props) {
  const [isConnectingGoogleDrive, setIsConnectingGoogleDrive] = useState(false);
  const {mutateAsync: manualSyncMutation, isLoading: isSyncing} = useMutation({
    mutationFn: manualSync,
    onSuccess: () => {
      toast.success('Successfully synced');
    },
    onError: (err) => {
      const message = get(err, 'response.data.message', 'Something went wrong');
      toast.error(message);
    },
    mutationKey: ['manual-sync'],
  });

  const handleConnectGoogleDrive = useCallback(async () => {
    try {
      setIsConnectingGoogleDrive(true);
      const {data} = await api.get('/integrations/google-drive/authorize/');
      window.open(data.auth_url, '_self');
      onConnect?.('google_drive');
    } catch (error) {
      const message = get(
        error,
        'response.data.message',
        'Something went wrong'
      );
      toast.error(message);
    } finally {
      setIsConnectingGoogleDrive(false);
    }
  }, [onConnect]);
  const {currentUser} = useAuth();

  const isGoogleDriveConnected = get(
    currentUser,
    'company.is_google_drive_connected'
  );

  const integrationCards = useMemo(
    () =>
      INTEGRATION_OPTIONS.map((option) => {
        const integrated = integrations?.find(
          (integration) =>
            integration.integration_type === option.integration_type
        );
        const sync = !!integrated
          ? () => manualSyncMutation(integrated?.id)
          : undefined;

        if (option.integration_type === 'google_drive') {
          return (
            <GoogleDriveCard
              key={option.integration_type}
              integration={option}
              isIntegrated={Boolean(isGoogleDriveConnected)}
              onConnect={handleConnectGoogleDrive}
              isConnecting={isConnectingGoogleDrive}
              onDisconnect={() =>
                onDisconnect?.({
                  ...option,
                  company_name: currentUser?.company?.name,
                  id: integrated?.id,
                } as unknown as Integration)
              }
            />
          );
        }
        return (
          <FeatureGuard
            key={option.integration_type}
            feature={'hasShopifyIntegration'}
          >
            <IntegrationCard
              integration={option}
              integratedItem={integrated}
              isIntegrated={Boolean(integrated)}
              onConnect={() => onConnect(option.integration_type)}
              manualSync={sync}
              isSyncing={isSyncing}
              onDisconnect={() => onDisconnect?.(integrated)}
            />
          </FeatureGuard>
        );
      }),
    [
      integrations,
      isSyncing,
      manualSyncMutation,
      onConnect,
      onDisconnect,
      isGoogleDriveConnected,
      currentUser?.company?.name,
      isConnectingGoogleDrive,
      handleConnectGoogleDrive,
    ]
  );
  return <div className={styles.integrationList}>{integrationCards}</div>;
}
export default IntegrationList;
